/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/shared/BulkActionBar.tsx
 * PURPOSE: CRM RecordTable bulk selection toolbar — assign owner, tag, export, delete, clear (contacts vs deals stage move)
 * DEPENDENCIES: flowbite-react, lucide-react, react, ../../data/scheduleModalCatalog, ../../types/opportunity.types, ./OwnerAvatar
 * PUBLIC_API: BulkActionBar, BulkActionBarVariant
 * SIDE_EFFECTS: User-triggered async handlers from parent
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: ~220/250
 * RECENT: 2026-05-08 [2.2.890] Left **`bulk-clear-selection-btn`** (`× {count} selected`); **`crm-bulk-selection-count`** — root **`CHANGELOG` [2.2.890]`**
 * RECENT_PRIOR: 2026-05-08 [2.2.889] Deals **Advance stage** — root **`CHANGELOG` [2.2.889]`**
 * RECENT_PRIOR: 2026-04-30 [2.2.750] Initial bulk bar for RecordTable multi-select — CHANGELOG [2.2.750]
 */

import { Button, Dropdown, DropdownItem, TextInput } from 'flowbite-react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useCallback, useId, useState } from 'react';
import { CRM_DEAL_STAGE_SELECT_OPTIONS } from '../../constants/crmDealStages';
import { MOCK_SCHEDULE_OWNERS } from '../../data/scheduleModalCatalog';
import type { OpportunityStage } from '../../types/opportunity.types';
import { OwnerAvatar } from './OwnerAvatar';

export type BulkActionBarVariant = 'contacts' | 'deals';

const DEAL_STAGE_OPTIONS = CRM_DEAL_STAGE_SELECT_OPTIONS;

export type BulkActionBarProps = {
  selectedIds: string[];
  variant: BulkActionBarVariant;
  onClear: () => void;
  onAssignOwner?: (ownerId: string) => void | Promise<void>;
  onAddTag?: (tag: string) => void | Promise<void>;
  onMoveStage?: (stage: OpportunityStage) => void | Promise<void>;
  /** Deals hub — advance each selected open deal to the next funnel stage (see **`DealsPage`**). */
  onAdvanceStage?: () => void | Promise<void>;
  onExport: () => void;
  onDelete: () => void | Promise<void>;
};

export function BulkActionBar({
  selectedIds,
  variant,
  onClear,
  onAssignOwner,
  onAddTag,
  onMoveStage,
  onAdvanceStage,
  onExport,
  onDelete,
}: BulkActionBarProps) {
  const count = selectedIds.length;
  const baseId = useId();
  const [tagDraft, setTagDraft] = useState('');
  const [tagBusy, setTagBusy] = useState(false);

  const applyTag = useCallback(async () => {
    const t = tagDraft.trim();
    if (!t || !onAddTag) return;
    setTagBusy(true);
    try {
      await onAddTag(t);
      setTagDraft('');
    } finally {
      setTagBusy(false);
    }
  }, [onAddTag, tagDraft]);

  const open = count > 0;

  return (
    <div
      data-testid="crm-bulk-action-bar"
      className={[
        'overflow-hidden transition-[max-height,opacity,margin] duration-200 ease-out motion-reduce:transition-none',
        open ? 'mb-3 max-h-40 opacity-100' : 'pointer-events-none mb-0 max-h-0 opacity-0',
      ].join(' ')}
      aria-hidden={!open}
    >
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 text-sm dark:border-primary-800 dark:bg-primary-900/20">
        <Button
          type="button"
          size="xs"
          color="gray"
          outline
          className="shrink-0 whitespace-nowrap"
          data-testid="bulk-clear-selection-btn"
          onClick={onClear}
          aria-label="Clear selection"
        >
          × {count} selected
        </Button>
        <span className="sr-only" data-testid="crm-bulk-selection-count">
          {count} selected
        </span>
        {variant === 'contacts' && onAssignOwner ? (
          <Dropdown
            inline
            placement="bottom-start"
            label=""
            dismissOnClick
            renderTrigger={() => (
              <Button type="button" size="xs" color="light" className="gap-1 whitespace-nowrap">
                Assign owner
                <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
              </Button>
            )}
          >
            {MOCK_SCHEDULE_OWNERS.map((o) => (
              <DropdownItem
                key={o.id}
                data-testid={`crm-bulk-assign-owner-${o.id}`}
                onClick={() => void onAssignOwner(o.id)}
              >
                <span className="flex items-center gap-2">
                  <OwnerAvatar name={o.name} size="xs" />
                  <span>{o.name}</span>
                </span>
              </DropdownItem>
            ))}
          </Dropdown>
        ) : null}
        {variant === 'contacts' && onAddTag ? (
          <Dropdown
            inline
            placement="bottom-start"
            label=""
            dismissOnClick={false}
            renderTrigger={() => (
              <Button type="button" size="xs" color="light" className="gap-1 whitespace-nowrap">
                Add tag
                <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
              </Button>
            )}
          >
            <div className="min-w-[12rem] space-y-2 px-3 py-2" onClick={(e) => e.stopPropagation()}>
              <label htmlFor={`${baseId}-bulk-tag`} className="sr-only">
                Tag
              </label>
              <TextInput
                id={`${baseId}-bulk-tag`}
                sizing="sm"
                placeholder="Tag"
                value={tagDraft}
                data-testid="crm-bulk-tag-input"
                onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void applyTag();
                  }
                }}
              />
              <Button
                type="button"
                size="xs"
                color="blue"
                className="w-full"
                disabled={tagBusy || !tagDraft.trim()}
                data-testid="crm-bulk-add-tag-apply"
                onClick={() => void applyTag()}
              >
                Apply tag
              </Button>
            </div>
          </Dropdown>
        ) : null}
        {variant === 'deals' && onAdvanceStage ? (
          <Button
            type="button"
            size="xs"
            color="light"
            className="gap-1 whitespace-nowrap"
            data-testid="bulk-advance-stage-btn"
            onClick={() => void onAdvanceStage()}
          >
            <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Advance stage
          </Button>
        ) : null}
        {variant === 'deals' && onMoveStage ? (
          <Dropdown
            inline
            placement="bottom-start"
            label=""
            dismissOnClick
            renderTrigger={() => (
              <Button type="button" size="xs" color="light" className="gap-1 whitespace-nowrap">
                Move stage
                <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
              </Button>
            )}
          >
            {DEAL_STAGE_OPTIONS.map((opt) => (
              <DropdownItem
                key={opt.value}
                data-testid={`crm-bulk-move-stage-${opt.value}`}
                onClick={() => void onMoveStage(opt.value)}
              >
                {opt.label}
              </DropdownItem>
            ))}
          </Dropdown>
        ) : null}
        <Button type="button" size="xs" color="light" data-testid="crm-bulk-export" onClick={onExport}>
          Export
        </Button>
        <Button type="button" size="xs" color="failure" data-testid="crm-bulk-delete" onClick={() => void onDelete()}>
          Delete
        </Button>
      </div>
    </div>
  );
}
